import React, { useCallback, useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format, isValid } from 'date-fns'
import Button from 'src/components/era-package/forms/Button'
import Buttons from 'src/components/era-package/era/Buttons'
import ChildCard from 'src/components/era-package/shared/ChildCard'
import DataTableSection from 'src/components/era-package/forms/DataTableSection'
import SnackBar from 'src/components/era-package/era/SnackBar'
import { useServerPagination } from 'src/views/abi/hooks/useServerPagination'
import AbiFeeFormulaApiUrls from 'src/apiUrls/AbiFeeFormulaApiUrls'
import { feeFormula, SUPPORTED_FORMULA_KEYWORDS } from 'src/views/abi/fields/fee-formula'
import { feeFormulaType, feeFormulaCode } from 'src/views/abi/fields/options'

const DEFAULT_PARAMS = { page: 0, size: 10, search: '' }

const optionsMap = { type: feeFormulaType, feeCode: feeFormulaCode }

const blankDraft = () => feeFormula.reduce((acc, field) => {
  acc[field.name] = field.defaultValue ?? ''
  return acc
}, {})

const validateRequired = (draft) => {
  const errors = {}
  feeFormula.forEach((field) => {
    if (field.required && !String(draft[field.name] ?? '').trim()) {
      errors[field.name] = `${field.label} is required`
    }
  })
  return errors
}

const extractFormulaKeywords = (formula) => {
  const keywords = []
  const regex = /\{(\w+)\}/g
  let match
  while ((match = regex.exec(formula || '')) !== null) {
    keywords.push(match[1])
  }
  return keywords
}

const rangesOverlap = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && bStart <= aEnd

// Client-side fast-feedback validation only - the backend overlap check (against the
// full table, not just this page) is the real authority, see plan B.6.
const validateFeeFormulaRow = (rowDraft, { rows, editingIndex }) => {
  const errors = {}

  const badKeywords = extractFormulaKeywords(rowDraft?.formula)
    .filter((keyword) => !SUPPORTED_FORMULA_KEYWORDS.includes(keyword.toLowerCase()))
  if (badKeywords.length > 0) {
    errors.formula = `Unsupported keyword: {${badKeywords[0]}}`
  }

  if (rowDraft?.type && rowDraft?.feeCode && rowDraft?.effectiveStartDate && rowDraft?.effectiveEndDate) {
    const overlaps = rows.some((row, idx) => {
      if (idx === editingIndex) return false
      if (row.type !== rowDraft.type || row.feeCode !== rowDraft.feeCode) return false
      return rangesOverlap(
        rowDraft.effectiveStartDate, rowDraft.effectiveEndDate,
        row.effectiveStartDate, row.effectiveEndDate
      )
    })
    if (overlaps) {
      const message = 'Overlaps an existing record for this Type/Fee (checked against this page only)'
      errors.effectiveStartDate = message
      errors.effectiveEndDate = message
    }
  }

  return errors
}

export const FeeFormulaTab = () => {
  // DataTableSection always mounts EditModal internally (even in editMode="row", where it
  // never opens), and EditModal calls useFieldArray/useFormContext unconditionally. Every
  // other DataTableSection consumer happens to sit inside a real RHF <FormProvider> already;
  // this page has no form of its own, so it needs this inert one just to satisfy that.
  const rhf = useForm()

  const { data, totalPages, params, fetchPage, updateParams } =
    useServerPagination(AbiFeeFormulaApiUrls.LIST, DEFAULT_PARAMS)

  const [rows, setRows] = useState([])
  const [showFormErrorSnackbar, setShowFormErrorSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const [addOpen, setAddOpen] = useState(false)
  const [addDraft, setAddDraft] = useState(blankDraft)
  const [addErrors, setAddErrors] = useState({})

  useEffect(() => {
    setRows(data)
  }, [data])

  const showApiError = useCallback((message) => {
    setErrorMessage(message || 'Internal error occurred, please try again later.')
    setShowFormErrorSnackbar(true)
  }, [])

  const showApiSuccess = useCallback((message) => {
    setSuccessMessage(message)
    setShowSuccessSnackbar(true)
  }, [])

  const persistCreateOrUpdate = useCallback(async (row, rollbackRows) => {
    const isCreate = row.id == null

    try {
      const response = await fetch(isCreate ? AbiFeeFormulaApiUrls.CREATE : AbiFeeFormulaApiUrls.UPDATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
        },
        body: JSON.stringify(row),
      })

      const json = await response.json().catch(() => null)

      if (!response.ok) {
        setRows(rollbackRows)
        showApiError(json?.message)
        return
      }

      showApiSuccess(isCreate ? 'Fee formula added.' : 'Fee formula updated.')
      fetchPage()
    } catch (error) {
      setRows(rollbackRows)
      showApiError()
    }
  }, [fetchPage, showApiError, showApiSuccess])

  const persistDelete = useCallback(async (id, rollbackRows) => {
    try {
      const response = await fetch(`${AbiFeeFormulaApiUrls.DELETE}?id=${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
        },
      })

      const json = await response.json().catch(() => null)

      if (!response.ok) {
        setRows(rollbackRows)
        showApiError(json?.message)
        return
      }

      showApiSuccess('Fee formula deleted.')
      fetchPage()
    } catch (error) {
      setRows(rollbackRows)
      showApiError()
    }
  }, [fetchPage, showApiError, showApiSuccess])

  const handleDataChange = useCallback((nextData) => {
    const previousRows = rows
    setRows(nextData)

    if (nextData.length > previousRows.length) {
      // A blank draft row was appended for editing; nothing to persist until Save.
      return
    }

    if (nextData.length === previousRows.length) {
      const changedIndex = nextData.findIndex((row, idx) => row !== previousRows[idx])
      if (changedIndex === -1) return
      persistCreateOrUpdate(nextData[changedIndex], previousRows)
      return
    }

    // A row was removed - only call the backend if it was a previously persisted row;
    // discarding an unsaved new-row draft (Cancel) never had an id.
    const removedRow = previousRows.find((row) => !nextData.includes(row))
    if (removedRow && removedRow.id != null) {
      persistDelete(removedRow.id, previousRows)
    }
  }, [rows, persistCreateOrUpdate, persistDelete])

  const openAddDialog = useCallback(() => {
    setAddDraft(blankDraft())
    setAddErrors({})
    setAddOpen(true)
  }, [])

  const closeAddDialog = useCallback(() => {
    setAddOpen(false)
  }, [])

  const handleAddFieldChange = useCallback((name, value, clearFields) => {
    setAddDraft((prev) => {
      const next = { ...prev, [name]: value }
      if (Array.isArray(clearFields)) {
        clearFields.forEach((f) => { next[f] = '' })
      }
      return next
    })
    setAddErrors((prev) => ({ ...prev, [name]: '' }))
  }, [])

  const handleAddSave = useCallback(() => {
    const errors = { ...validateRequired(addDraft), ...validateFeeFormulaRow(addDraft, { rows, editingIndex: null }) }
    if (Object.keys(errors).length > 0) {
      setAddErrors(errors)
      return
    }

    const optimisticRows = [...rows, addDraft]
    setRows(optimisticRows)
    persistCreateOrUpdate(addDraft, rows)
    setAddOpen(false)
  }, [addDraft, rows, persistCreateOrUpdate])

  const feeOptionsForType = feeFormulaCode.filter((option) => option.code === addDraft.type)

  return (
    <FormProvider {...rhf}>
      <ChildCard>
        <Box display="flex" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={openAddDialog}
            startIcon={<FontAwesomeIcon icon={faPlus} width={12} />}
            title="Add a new Fee Formula record"
          >
            Add
          </Button>
        </Box>

        <DataTableSection
          title="Fee Formula"
          name="feeFormula"
          fields={feeFormula}
          optionsMap={optionsMap}
          data={rows}
          onDataChange={handleDataChange}
          editMode="row"
          disableAdd
          customValidate={validateFeeFormulaRow}
          pagination={{
            pageIndex: params.page,
            pageSize: params.size,
            totalPages,
            onPageChange: (pageIndex) => updateParams({ page: pageIndex }),
            onPageSizeChange: (pageSize) => updateParams({ size: pageSize, page: 0 }),
          }}
        />
      </ChildCard>

      <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Fee Formula</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              select
              label="Type"
              fullWidth
              required
              value={addDraft.type}
              error={!!addErrors.type}
              helperText={addErrors.type}
              onChange={(event) => handleAddFieldChange('type', event.target.value, ['feeCode'])}
            >
              {feeFormulaType.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.description}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Fee"
              fullWidth
              required
              disabled={!addDraft.type}
              value={addDraft.feeCode}
              error={!!addErrors.feeCode}
              helperText={addErrors.feeCode || (!addDraft.type ? 'Select Type first' : '')}
              onChange={(event) => handleAddFieldChange('feeCode', event.target.value)}
            >
              {feeOptionsForType.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.value} - {option.description}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Rate"
              fullWidth
              value={addDraft.rate}
              error={!!addErrors.rate}
              helperText={addErrors.rate}
              onChange={(event) => handleAddFieldChange('rate', event.target.value)}
            />

            <TextField
              label="Formula"
              fullWidth
              required
              placeholder="e.g. 0.01327 *{qtyofckg}"
              value={addDraft.formula}
              error={!!addErrors.formula}
              helperText={addErrors.formula}
              onChange={(event) => handleAddFieldChange('formula', event.target.value)}
            />

            <TextField
              label="Print"
              fullWidth
              required
              value={addDraft.printText}
              error={!!addErrors.printText}
              helperText={addErrors.printText}
              onChange={(event) => handleAddFieldChange('printText', event.target.value)}
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <DesktopDatePicker
                  label="Effective Start Date"
                  inputFormat="MM-dd-yyyy"
                  value={addDraft.effectiveStartDate ? new Date(addDraft.effectiveStartDate) : null}
                  onChange={(date) => {
                    if (date && !isValid(date)) return
                    handleAddFieldChange('effectiveStartDate', date ? format(date, 'yyyy-MM-dd') : '')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      error={!!addErrors.effectiveStartDate}
                      helperText={addErrors.effectiveStartDate}
                    />
                  )}
                />
                <DesktopDatePicker
                  label="Effective End Date"
                  inputFormat="MM-dd-yyyy"
                  value={addDraft.effectiveEndDate ? new Date(addDraft.effectiveEndDate) : null}
                  onChange={(date) => {
                    if (date && !isValid(date)) return
                    handleAddFieldChange('effectiveEndDate', date ? format(date, 'yyyy-MM-dd') : '')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      error={!!addErrors.effectiveEndDate}
                      helperText={addErrors.effectiveEndDate}
                    />
                  )}
                />
              </Stack>
            </LocalizationProvider>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pt: 1 }}>
          <Buttons
            advance={
              <Button
                variant="contained"
                onClick={handleAddSave}
                startIcon={<FontAwesomeIcon icon={faPlus} width={12} />}
                title="Add this record"
              >
                Add
              </Button>
            }
            other={
              <Button variant="outlined" onClick={closeAddDialog} title="Go back">
                Cancel
              </Button>
            }
          />
        </DialogActions>
      </Dialog>

      <SnackBar
        title="Error"
        type="error"
        open={showFormErrorSnackbar}
        onClose={() => setShowFormErrorSnackbar(false)}
        duration={10000}
      >
        {errorMessage || 'Internal error occurred, please try again later.'}
      </SnackBar>

      <SnackBar
        title="Success"
        type="success"
        open={showSuccessSnackbar}
        onClose={() => setShowSuccessSnackbar(false)}
        duration={4000}
      >
        {successMessage}
      </SnackBar>
    </FormProvider>
  )
}
